import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { get } from 'lodash-es'
import { chatbotEvents } from '../state'
import { portfolioApiEvents } from 'modules/api/portfolio'

export interface LeaseRentAndFeesState {
  keyDepositAmount: string
  serviceDepositAmount: string
  damageDepositAmount: string
  monthlyRentAmount: string
  leaseFee: {
    netAmount: string
    vatable: boolean
  }
  applicationFee: {
    netAmount: string
    vatable: boolean
  }
  administrationFee: {
    netAmount: string
    vatable: boolean
  }
  firstMonthRentAmount: string
  depositHeldByLandlord?: boolean
}

export const initialState: LeaseRentAndFeesState = {
  keyDepositAmount: '',
  serviceDepositAmount: '',
  damageDepositAmount: '',
  monthlyRentAmount: '',
  leaseFee: {
    netAmount: '',
    vatable: false,
  },
  applicationFee: {
    netAmount: '',
    vatable: false,
  },
  administrationFee: {
    netAmount: '',
    vatable: false,
  },
  firstMonthRentAmount: '',
  depositHeldByLandlord: false,
}

const leaseRentAndFeesSlice = createSlice({
  name: 'chatbot/dialogs/leaseRentAndFees',
  initialState,
  reducers: {
    rentAndFeesSaved: (state, action: PayloadAction<Partial<LeaseRentAndFeesState>>) => {
      Object.assign(state, action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(chatbotEvents.setGlobalVat, (state, action) => {
        state.leaseFee.vatable = action.payload
        state.applicationFee.vatable = action.payload
        state.administrationFee.vatable = action.payload
      })
      .addCase(portfolioApiEvents.amendRentAndFees_success, () => {
        // No-op - matches factory behavior ($merge: {})
      })
      .addCase(chatbotEvents.setCurrentPortfolio, (state, action) => {
        const container = get(action.payload, 'contractContainer') || action.payload
        const terms = get(container, 'value.terms', false)

        if (terms) {
          state.keyDepositAmount = get(terms, 'keyDepositAmount', '')
          state.serviceDepositAmount = get(terms, 'serviceDepositAmount', '')
          state.damageDepositAmount = get(terms, 'damageDepositAmount', '')
          state.monthlyRentAmount = get(terms, 'monthlyRentAmount', '')
          state.leaseFee = {
            netAmount: get(terms, 'leaseFee.netAmount', ''),
            vatable: get(terms, 'leaseFee.vatable', state.leaseFee.vatable),
          }
          state.applicationFee = {
            netAmount: get(terms, 'applicationFee.netAmount', ''),
            vatable: get(terms, 'applicationFee.vatable', state.applicationFee.vatable),
          }
          state.administrationFee = {
            netAmount: get(terms, 'administrationFee.netAmount', ''),
            vatable: get(terms, 'administrationFee.vatable', state.administrationFee.vatable),
          }
          state.firstMonthRentAmount = get(terms, 'firstMonthRentAmount', '')
          state.depositHeldByLandlord = get(terms, 'depositHeldByLandlord', false)
        }
      })
      .addCase(chatbotEvents.resetCurrentPortfolio, state => {
        // CRITICAL: Preserve VAT settings across reset
        const leaseFeeVatable = state.leaseFee.vatable
        const applicationFeeVatable = state.applicationFee.vatable
        const administrationFeeVatable = state.administrationFee.vatable
        // Reset scalar properties
        state.keyDepositAmount = initialState.keyDepositAmount
        state.serviceDepositAmount = initialState.serviceDepositAmount
        state.damageDepositAmount = initialState.damageDepositAmount
        state.monthlyRentAmount = initialState.monthlyRentAmount
        state.firstMonthRentAmount = initialState.firstMonthRentAmount
        state.depositHeldByLandlord = initialState.depositHeldByLandlord
        // Reset nested objects with preserved VAT values
        state.leaseFee = { netAmount: '', vatable: leaseFeeVatable }
        state.applicationFee = { netAmount: '', vatable: applicationFeeVatable }
        state.administrationFee = { netAmount: '', vatable: administrationFeeVatable }
      })
  },
})

export const { reducer } = leaseRentAndFeesSlice
export const leaseRentAndFeesEvents = leaseRentAndFeesSlice.actions
